import * as AccordionPrimitive from '@radix-ui/react-accordion'
import { ChevronDown, Minus, Plus } from 'lucide-react'
import * as React from 'react'
import { twMerge } from 'tailwind-merge'
import { tv, VariantProps } from 'tailwind-variants'

const accordionItemVariants = tv({
  base: '',
  variants: {
    variant: {
      integration: '*:*:bg-transparent',
      default: '*:*:bg-monochromatic-200',
      withBackgroundWhite: '*:*:bg-white',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

const accordionTriggerVariants = tv({
  slots: {
    container:
      'flex flex-1 group items-center duration-300 justify-between rounded-lg border border-monochromatic-200 px-5 py-4 text-left text-sm font-medium transition-all data-[state=open]:rounded-b-none data-[state=closed]:drop-shadow-md data-[state=closed]:hover:border-bios-light data-[state=closed]:hover:shadow-lg [&[data-state=open]>svg]:rotate-180',
    icon: 'size-4 shrink-0 duration-300 md:size-5 2xl:size-6',
  },

  variants: {
    variant: {
      integration: {
        container:
          'border-none data-[state=closed]:hover:shadow-none px-0 pr-2',
      },
      green: {
        container:
          'text-bios-primary text-lg border-none data-[state=closed]:drop-shadow-none data-[state=closed]:hover:shadow-md',
        icon: 'text-bios-primary absolute transition-all ease-in-out',
      },
      default: {
        container: 'text-accordion-title',
        icon: 'text-accordion-title transition-transform ease-linear',
      },
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

const Accordion = AccordionPrimitive.Root

interface AccordionItemProps
  extends React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>,
    VariantProps<typeof accordionItemVariants> {}

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  AccordionItemProps
>(({ className, variant, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={twMerge(accordionItemVariants({ variant }), className)}
    {...props}
  />
))
AccordionItem.displayName = 'AccordionItem'

interface AccordionTriggerProps
  extends React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>,
    VariantProps<typeof accordionTriggerVariants> {
  isWithIconPlusAndMinus?: boolean
}

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  AccordionTriggerProps
>(
  (
    { className, children, variant, isWithIconPlusAndMinus = false, ...props },
    ref,
  ) => {
    const { container, icon } = accordionTriggerVariants({ variant })
    return (
      <AccordionPrimitive.Header className="flex">
        <AccordionPrimitive.Trigger
          ref={ref}
          className={twMerge(container(), className)}
          {...props}
        >
          {children}

          {isWithIconPlusAndMinus ? (
            <div className="bg-bios-overlay-600 relative flex items-center justify-center rounded-md p-3 md:p-4">
              <Plus
                className={twMerge(
                  icon(),
                  'scale-100 rotate-0 opacity-100',
                  'group-data-[state=open]:scale-75 group-data-[state=open]:rotate-90 group-data-[state=open]:opacity-0',
                )}
              />
              <Minus
                className={twMerge(
                  icon(),
                  'scale-75 rotate-90 opacity-0',
                  'group-data-[state=open]:scale-100 group-data-[state=open]:rotate-0 group-data-[state=open]:opacity-100',
                )}
              />
            </div>
          ) : (
            <ChevronDown className={icon()} />
          )}
        </AccordionPrimitive.Trigger>
      </AccordionPrimitive.Header>
    )
  },
)
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden rounded-b-lg text-sm"
    {...props}
  >
    <div className={twMerge('px-5 pt-0 pb-4', className)}>{children}</div>
  </AccordionPrimitive.Content>
))
AccordionContent.displayName = AccordionPrimitive.Content.displayName

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent }
